import type { ApplicationDocument, ApplicationFormDocument, FieldAnswer } from '@/types'
import { typedCollection, getCollection, where, orderBy } from '@/lib/firebase/firestore'
import { ref, uploadBytesResumable } from '@/lib/firebase/storage'
import { storage } from '@/lib/firebase/storage'
import {
  prepareApplicationUploadFn,
  prepareTechQuestionnaireUploadFn,
  submitApplicationFn,
  type ApplicationUploadReceipt,
} from '@/lib/firebase/functions'

const appCol = () => typedCollection<ApplicationDocument>('applications')
const formCol = () => typedCollection<ApplicationFormDocument>('applicationForms')

export interface TechQuestionnaireUploadContext {
  productionSlug: string
  submissionId: string
  token: string
}

export const applicationsService = {
  async getPublished(): Promise<Array<ApplicationDocument & { id: string }>> {
    // Defensive filter: tech_questionnaire forms are never publicly listed —
    // they're rendered only inline on the production confirmation page through
    // a token-validated path. Belt-and-suspenders against an admin accidentally
    // flipping isPublished on a tech form.
    const all = await getCollection(
      appCol(),
      where('isPublished', '==', true),
      orderBy('order', 'asc')
    )
    return all.filter((a) => a.applicationType !== 'tech_questionnaire')
  },

  async getBySlug(slug: string): Promise<(ApplicationDocument & { id: string }) | null> {
    const results = await getCollection(
      appCol(),
      where('slug', '==', slug),
      where('isPublished', '==', true)
    )
    const first = results[0] ?? null
    if (first?.applicationType === 'tech_questionnaire') return null
    return first
  },

  async getFormByApplicationId(
    applicationId: string
  ): Promise<(ApplicationFormDocument & { id: string }) | null> {
    const results = await getCollection(formCol(), where('applicationId', '==', applicationId))
    return results[0] ?? null
  },

  async uploadFile(
    applicationId: string,
    formId: string,
    fieldId: string,
    file: File,
    onProgress?: (pct: number) => void,
    techContext?: TechQuestionnaireUploadContext
  ): Promise<ApplicationUploadReceipt> {
    const uploadInput = {
      applicationId,
      formId,
      fieldId,
      fileName: file.name,
      contentType: file.type,
      sizeBytes: file.size,
    }
    const grant = techContext
      ? await prepareTechQuestionnaireUploadFn({ ...techContext, ...uploadInput })
      : await prepareApplicationUploadFn(uploadInput)
    const storageRef = ref(storage, grant.data.objectPath)
    return new Promise((resolve, reject) => {
      const task = uploadBytesResumable(storageRef, file, {
        contentType: file.type,
        customMetadata: {
          firebaseStorageDownloadTokens: grant.data.downloadToken,
        },
      })
      task.on(
        'state_changed',
        (snap) => {
          onProgress?.(Math.round((snap.bytesTransferred / snap.totalBytes) * 100))
        },
        reject,
        () =>
          resolve({
            uploadId: grant.data.uploadId,
            claimToken: grant.data.claimToken,
            url: grant.data.downloadUrl,
          })
      )
    })
  },

  async submit(payload: {
    applicationId: string
    formId: string
    applicantInfo: { firstName: string; lastName: string; email: string }
    answers: Record<string, FieldAnswer>
    fileUploads: Record<string, ApplicationUploadReceipt[]>
    termsAccepted: boolean
    submissionRequestId?: string
  }): Promise<string> {
    const res = await submitApplicationFn(payload)
    return res.data.submissionId
  },
}
